'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useRef, useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { Effect, Gender, StoryLine } from '@/.gql/graphql';
import {
  GENERATE_PHOTO_SESSION,
  GENERATE_STYLE,
  GENERATE_VIDEO_LOOK,
  GENERATE_VIDEO_STYLE,
  GENERATE_COMBI_VIDEO,
  GENERATE_EFFECT,
  GENERATE_VIDEO_VEO3,
  SEND_FREE_STICKER_PACK,
  GENERATE_STORY_LINE,
  GENERATE_PHOTO_SESSION_PHOTO,
} from '@/.graphql';
import { Avatars } from '@/src/components/Avatars';
import { Header } from '@/src/components/Header';
import { PromptArea } from '@/src/components/PromptArea';
import { VideoAvatars } from '@/src/components/VideoAvatars';
import { AvatarYourPhoto } from '@/src/components/AvatarYourPhoto';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  TAiVideo,
  useMobileContext,
} from '@/src/providers';
import { getBlockName } from '@/src/utils/getBlockName';
import { getSource } from '@/src/utils/getSource';

import { AspectRatio } from './AspectRatio';
import { ButtonsGroup } from './ButtonsGroup';
import { CloseAppButton } from './CloseAppButton';
import { EmblaCarousel } from './EmblaCarousel';
import { Examples } from './Examples';

const styles = {
  portal: '!max-h-screen !h-screen !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center',
  wrapper: 'shrink-0 bg-fill-menu',
  innerWrap: 'relative p-5',
  info: 'flex flex-col gap-4 p-4 mb-4 bg-fill-quinary rounded-xl',
  chooseTitle: 'text-base font-bold mb-3',
  exampleTitle: 'mb-4',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
};

export const GenerateImagePortal: FC = () => {
  const t = useTranslations('imageModal');
  const {
    genderMode,
    activeAvatar,
    activeAspectRatio,
    portalSeeAllMode,
    activeStyle,
    prevPortal,
    filteredAvatars,
    isYourPhoto,
    activeCategory,
    currentPack,
    isPhotosessionPhoto,
  } = useStyleContext();
  const { isMobiles } = useMobileContext();
  const currentIndex = useMemo(
    () => currentPack?.findIndex((style) => style.id === activeStyle?.id),
    [currentPack, activeStyle],
  );

  const vitrinaPosition = useVitrinaPosition(portalSeeAllMode);

  const profile = useProfileContext()!;

  const sendGTM = useGTMContext();
  const { closePortal, openPortal, currentPortal } = usePortalContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [styleGeneration, { loading }] = useMutation(GENERATE_STYLE);
  const [sendFreeStickerPack] = useMutation(SEND_FREE_STICKER_PACK);
  const [photoSessionGeneration, { loading: photoSessionGenerationLoading }] =
    useMutation(GENERATE_PHOTO_SESSION);
  const [videoLookGeneration, { loading: videoLookGenerationLoading }] =
    useMutation(GENERATE_VIDEO_LOOK);
  const [videoVeo3Generation, { loading: videoVeo3GenerationLoading }] =
    useMutation(GENERATE_VIDEO_VEO3);
  const [videoStyleGeneration, { loading: videoStyleGenerationLoading }] =
    useMutation(GENERATE_VIDEO_STYLE, {
      context: {
        headers: {
          'apollo-require-preflight': true,
        },
      },
    });
  const [generateStoryLine, { loading: generateStoryLineLoading }] =
    useMutation(GENERATE_STORY_LINE, {
      context: {
        headers: {
          'apollo-require-preflight': true,
        },
      },
    });
  const [combiVideoGeneration, { loading: combiVideoGenerationLoading }] =
    useMutation(GENERATE_COMBI_VIDEO, {
      context: {
        headers: {
          'apollo-require-preflight': true,
        },
      },
    });
  const [generateEffect, { loading: generateEffectLoading }] = useMutation(
    GENERATE_EFFECT,
    {
      context: {
        headers: {
          'apollo-require-preflight': true,
        },
      },
    },
  );
  const [
    generatePhotoSessionPhoto,
    { loading: generatePhotoSessionPhotoLoading },
  ] = useMutation(GENERATE_PHOTO_SESSION_PHOTO, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });

  const [videoStyleImage, setVideoStyleImage] = useState<File[] | File | null>(
    null,
  );

  const [effectPrompt, setEffectPrompt] = useState<string>('');
  const [effectAccessory, setEffectAccessory] = useState<string>('');

  const toastSuccessPhotoAddedIdRef = useRef<string | number | null>(null);

  const handleUploadButtonClick = () => {
    sendGTM({
      event: 'upload_photo',
      type: activeStyle?.__typename!,
      block: getBlockName(portalSeeAllMode),
      category_name:
        activeStyle?.__typename === 'Style' ? activeCategory?.name['en'] : null,
      name: activeStyle?.name['en'],
      category_position: currentIndex + 1,
      vitrina_position: vitrinaPosition,
      gender: genderMode,
      source: getSource(prevPortal),
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = event.target.files
      ? Array.from(event.target.files)
      : [];

    const file = event.target.files?.[0];

    if (activeStyle?.__typename === 'Effect') {
      setVideoStyleImage(files);
    } else {
      if (file) {
        setVideoStyleImage(file);
      }
    }

    if (
      activeStyle?.__typename === 'Effect' &&
      (activeStyle as Effect).requiredImageCount === 2 &&
      files.length !== 2
    ) {
      toast.error(t('uploadTwoPhotos'));
      setVideoStyleImage(null);
      return;
    }

    const toastText = ['StoryLine', 'VideoStyle', 'PhotoSession'].includes(
      activeStyle?.__typename!,
    )
      ? t('successPhotoAdded')
      : activeStyle?.__typename === 'Effect'
        ? `${t('successPhotoAdded')} ${activeStyle?.isInputUserName ? t('successNextStep') : ''}`
        : t('successPhotoPartnerAdded');

    sendGTM({
      event: 'upload_photo_complete',
      type: activeStyle?.__typename!,
      block: getBlockName(portalSeeAllMode),
      category_name:
        activeStyle?.__typename === 'Style' ? activeCategory?.name['en'] : null,
      name: activeStyle?.name['en'],
      category_position: currentIndex + 1,
      vitrina_position: vitrinaPosition,
      gender: genderMode,
      source: getSource(prevPortal),
    });

    toastSuccessPhotoAddedIdRef.current = toast(
      <span className="whitespace-pre-line">{toastText}</span>,
      {
        autoClose: 1000,
        position: 'bottom-center',
        icon: (
          <Image
            width={16}
            height={18}
            src="/icons/check.svg"
            alt={activeStyle?.name[profile?.lang]}
            className="w-4 h-4"
          />
        ),
        theme: 'dark',
        style: {
          backgroundColor: '#3D3E41',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '500',
          borderRadius: '12px',
          padding: '16px',
          bottom: '32px',
          maxWidth: 'calc(100% - 40px)',
          alignItems: 'start',
        },
      },
    );
  };

  const handleClickGenerate = async () => {
    try {
      setButtonDisabled(true);

      if (toastSuccessPhotoAddedIdRef.current) {
        toast.dismiss(toastSuccessPhotoAddedIdRef.current);
        toastSuccessPhotoAddedIdRef.current = null;
      }

      sendGTM({
        event: 'Generate',
        type: activeStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name:
          activeStyle?.__typename === 'Style'
            ? activeCategory?.name['en']
            : null,
        name: activeStyle?.name['en'],
        category_position: currentIndex + 1,
        vitrina_position: vitrinaPosition,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      if (
        activeStyle?.__typename === 'Effect' &&
        activeStyle.isFreeStickerPack
      ) {
        await sendFreeStickerPack({
          variables: {
            effectId: activeStyle.id,
          },
        });

        toast(<span>{t('successFreeStickerPackAdded')}</span>, {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt={activeStyle?.name[profile?.lang]}
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        });

        return;
      }

      const { data } = ['VideoLook', 'VideoStyle'].includes(
        activeStyle?.__typename!,
      )
        ? isYourPhoto
          ? await videoStyleGeneration({
              variables: {
                videoStyleId: (activeStyle as TAiVideo)?.videoStyleId!,
                image: videoStyleImage,
                resolution: activeAspectRatio,
              },
            })
          : await videoLookGeneration({
              variables: {
                videoLookId: (activeStyle as TAiVideo)?.videoLookId!,
                loraId: activeAvatar,
                resolution: activeAspectRatio,
              },
            })
        : activeStyle?.__typename === 'PhotoSession'
          ? isPhotosessionPhoto
            ? await generatePhotoSessionPhoto({
                variables: {
                  photoSessionId: activeStyle?.id,
                  image: videoStyleImage,
                  resolution: activeAspectRatio,
                },
              })
            : await photoSessionGeneration({
                variables: {
                  photoSessionId: activeStyle?.id,
                  loraId: activeAvatar,
                  resolution: activeAspectRatio,
                },
              })
          : activeStyle?.__typename === 'CombiVideo'
            ? await combiVideoGeneration({
                variables: {
                  combiVideoId: activeStyle?.id,
                  loraId: activeAvatar,
                  image: videoStyleImage,
                  resolution: activeAspectRatio,
                },
              })
            : activeStyle?.__typename === 'Effect'
              ? await generateEffect({
                  variables: {
                    effectId: activeStyle?.id,
                    images: videoStyleImage,
                    resolution: activeAspectRatio,
                    userName: effectPrompt,
                    gender: genderMode,
                    accessory: effectAccessory,
                  },
                })
              : activeStyle?.__typename === 'StoryLine'
                ? await generateStoryLine({
                    variables: {
                      input: {
                        storyLineId: activeStyle?.id!,
                        image: videoStyleImage!,
                      },
                    },
                  })
                : activeStyle?.__typename === 'VideoVeo3'
                  ? await videoVeo3Generation({
                      variables: {
                        videoVeo3Id: activeStyle?.id,
                        userPrompt: effectPrompt,
                      },
                    })
                  : await styleGeneration({
                      variables: {
                        styleId: activeStyle?.id,
                        loraId: activeAvatar,
                        resolution: activeAspectRatio,
                      },
                    });

      if (!data) {
        throw new Error('No data');
      }

      let queueTime = 0;

      if ('generateStyle' in data) {
        queueTime = data.generateStyle.queueTime;
      } else if ('generatePhotoSession' in data) {
        queueTime = data.generatePhotoSession.queueTime;
      } else if ('generateVideoStyle' in data) {
        queueTime = data.generateVideoStyle.queueTime;
      } else if ('generateVideoLook' in data) {
        queueTime = data.generateVideoLook.queueTime;
      } else if ('generateEffect' in data) {
        queueTime = data.generateEffect.queueTime;
      } else if ('generateVideoVeo3' in data) {
        queueTime = data.generateVideoVeo3.queueTime;
      } else if ('generateStoryLine' in data) {
        queueTime = data.generateStoryLine.queueTime;
      } else if ('generatePhotoSessionPhoto' in data) {
        queueTime = data.generatePhotoSessionPhoto.queueTime;
      } else {
        queueTime = data.generateCombiVideo.queueTime;
      }

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt={activeStyle?.name[profile?.lang]}
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Generation_complete',
        type: activeStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name:
          activeStyle?.__typename === 'Style'
            ? activeCategory?.name['en']
            : null,
        name: activeStyle?.name['en'],
        category_position: currentIndex + 1,
        vitrina_position: vitrinaPosition,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setVideoStyleImage(null);
      }, 5000);
    } catch (err) {
      const error = err as Error;
      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Generation_error',
        type: activeStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name:
          activeStyle?.__typename === 'Style'
            ? activeCategory?.name['en']
            : null,
        name: activeStyle?.name['en'],
        category_position: currentIndex + 1,
        vitrina_position: vitrinaPosition,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setVideoStyleImage(null);
      }, 5000);
    }
  };

  const examples = useMemo(() => {
    if (activeStyle?.__typename! !== 'StoryLine') {
      return [];
    }

    return genderMode === Gender.Female
      ? (activeStyle as StoryLine)?.examplesWoman || []
      : (activeStyle as StoryLine)?.examplesMan || [];
  }, [activeStyle, genderMode]);

  const infoText = useMemo(() => {
    switch (activeStyle?.__typename) {
      case 'Style':
        return t('info.text');
      case 'PhotoSession':
        return t('info.photoshootText');
      case 'Effect':
        return activeStyle?.isStickerPack
          ? t('info.stickerpackText')
          : t('info.photoByPhotoText');
      case 'VideoLook':
      case 'VideoStyle':
      case 'CombiVideo':
      case 'VideoVeo3':
        return t('info.videoText');
      case 'StoryLine':
        return t.rich('info.storyLinesText', {
          count: examples.length,
        });
      default:
        return;
    }
  }, [activeStyle, t]);

  const handleClosePortal = () => {
    if (prevPortal === 'main') {
      closePortal();
    } else if (prevPortal === 'trending') {
      closePortal();
      openPortal(PORTALS.SeeAllTrending);
    } else if (prevPortal === 'storyline') {
      closePortal();
      openPortal(PORTALS.SeeAllStorylines);
    } else {
      closePortal();
      openPortal(PORTALS.SeeAllImages);
    }
  };

  const disabled =
    buttonDisabled ||
    loading ||
    photoSessionGenerationLoading ||
    videoLookGenerationLoading ||
    videoStyleGenerationLoading ||
    combiVideoGenerationLoading ||
    videoVeo3GenerationLoading ||
    generateStoryLineLoading ||
    generatePhotoSessionPhotoLoading ||
    generateEffectLoading;

  const isVideoStyleType =
    activeStyle?.__typename === 'VideoLook' ||
    activeStyle?.__typename === 'VideoStyle';

  const isEffect = activeStyle?.__typename === 'Effect';

  const isVeo3 = activeStyle?.__typename === 'VideoVeo3';

  const isStoryLine = activeStyle?.__typename === 'StoryLine';

  const showAspectRatio =
    (isEffect && !videoStyleImage && !activeStyle.isStickerPack) ||
    (isVideoStyleType && (activeStyle as TAiVideo)?.showResolutions) ||
    (!isEffect && !isVideoStyleType && !isVeo3 && !isStoryLine);

  useEffect(() => {
    if (currentPortal === PORTALS.GenerateImage) {
      sendGTM({
        event: 'popap_generation',
        type: activeStyle?.__typename!,
        block: getBlockName(portalSeeAllMode),
        category_name: [
          'styles',
          'effectsCategories',
          'videoStyleCategories',
        ].includes(portalSeeAllMode)
          ? activeCategory?.name['en']
          : null,
        name: activeStyle?.name['en'],
        category_position: currentIndex + 1,
        gender: genderMode,
        source: getSource(prevPortal),
      });
    }
  }, [
    activeCategory,
    activeStyle,
    currentIndex,
    currentPack,
    currentPortal,
    genderMode,
    portalSeeAllMode,
    prevPortal,
    sendGTM,
  ]);

  if (!activeStyle) {
    return null;
  }

  return (
    <PortalModal
      portalId={PORTALS.GenerateImage}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <EmblaCarousel />

            <div className={styles.innerWrap}>
              <div className={styles.info}>
                {['CombiVideo', 'PhotoSession', 'Style'].includes(
                  activeStyle?.__typename!,
                ) &&
                  (isPhotosessionPhoto ? (
                    <AvatarYourPhoto />
                  ) : (
                    <Avatars avatars={filteredAvatars} />
                  ))}

                {isVideoStyleType && <VideoAvatars avatars={filteredAvatars} />}

                <div>
                  <div className={styles.infoTitle}>{t('info.title')}</div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>{infoText}</li>
                  </ul>
                </div>
                {((isEffect && !activeStyle.isFreeStickerPack) ||
                  isPhotosessionPhoto) && (
                  <div>
                    <div className={styles.infoTitle}>
                      {t('info.howItWorks.title')}
                    </div>
                    <ul className={styles.infoList}>
                      <li className={styles.infoListItem}>
                        {isEffect
                          ? (activeStyle as Effect).isStickerPack
                            ? t('info.howItWorks.stickerpackText')
                            : (activeStyle as Effect).requiredImageCount === 2
                              ? t('info.howItWorks.effects2photoText')
                              : t('info.howItWorks.effectsText')
                          : t('info.howItWorks.photoshootText')}
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {isEffect && videoStyleImage ? (
                <div className="flex flex-col gap-2">
                  {activeStyle.isInputUserName && (
                    <PromptArea
                      changePrompt={setEffectPrompt}
                      prompt={effectPrompt}
                      placeholder={t('promptPlaceholder')}
                      maxLength={30}
                      className="!bg-fill-quinary !font-normal !rounded-xl !min-h-12 !h-12 !border-yellow"
                    />
                  )}
                  {activeStyle.isInputAccessory && (
                    <PromptArea
                      changePrompt={setEffectAccessory}
                      prompt={effectAccessory}
                      placeholder={t('accessoryPlaceholder')}
                      maxLength={30}
                      className="!bg-fill-quinary !font-normal !rounded-xl !min-h-12 !h-12 !border-yellow"
                    />
                  )}
                </div>
              ) : (
                showAspectRatio && <AspectRatio />
              )}

              {isVeo3 && (
                <PromptArea
                  changePrompt={setEffectPrompt}
                  prompt={effectPrompt}
                  placeholder={t('promptVideoVeo3Placeholder')}
                  maxLength={2500}
                  className="!bg-fill-quinary !text-sm !font-normal placeholder:!leading-6 !rounded-xl !min-h-[208px] !h-[208px] !border-yellow"
                />
              )}

              {['StoryLine', 'PhotoSession'].includes(
                activeStyle?.__typename!,
              ) && <Examples />}

              {profile?.isAcitveStartFunnel ? (
                <CloseAppButton
                  text={t('buttonPay')}
                  keybord="paymentDownSale4"
                />
              ) : (
                <ButtonsGroup
                  disabled={disabled}
                  handleClickGenerate={handleClickGenerate}
                  handleFileChange={handleFileChange}
                  videoStyleImage={videoStyleImage}
                  effectPrompt={effectPrompt}
                  effectAccessory={effectAccessory}
                  storyLinesLength={examples?.length}
                  onClickUploadButton={handleUploadButtonClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
